import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import { BrokenRuleBanner } from "../components/BrokenRuleBanner";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { latestSuccessAttempt } from "../lib/after-auth";
import { getApi } from "../lib/get-api";
import {
  accessOf,
  accessRequestBody,
  canManageMembers,
  alignTabRules,
  brokenRuleBannerLabel,
  DEFAULT_INVITE_ROLE,
  filterAsMemberEmail,
  filterFromDraft,
  filterValueDraft,
  filterWithOp,
  filterWithoutMemberEmail,
  firstBrokenMember,
  incompleteRowFilters,
  INVITABLE_ROLES,
  InvitableRole,
  inviteLink,
  inviteNotice,
  isOwnerRow,
  lastActiveLabel,
  Member,
  memberDisplayName,
  memberListSummary,
  parseEmailList,
  roleChoiceLabel,
  rolePreviewLine,
  roleLabel,
  ROW_FILTER_OPS,
  RowFilterOp,
  rowFilterGapMessage,
  sortMembers,
  TAB_LEVELS,
  tabLevelLabel,
  TabRule,
  ViewerRole,
  totalBrokenRules,
  usesMemberEmail,
} from "../lib/member-access";
import {
  hrefWithPreview,
  parsePreviewBody,
  previewButtonLabel,
  previewFailureMessage,
  rememberPreviewName,
} from "../lib/preview-as";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";

/**
 * Share: who can see this panel, and what they can do (MOSA-367 slice 1).
 *
 * Reached from the Share button in the panel header. Only the owner gets here
 * with anything to do — the API answers 403 to a member on every route this
 * page calls, and the page says so rather than showing an empty list.
 *
 * Role names on the wire are the stored ones (`MEMBER` is an editor). This
 * page speaks `EDITOR` / `VIEWER` / `CUSTOM` and lets `access` off the API do
 * the translation. Slice 1 still sends `accessRole`.
 */

const fieldClass =
  "min-h-11 max-w-full rounded-md border border-od-border bg-od-surface px-2 text-sm text-od-text";

const OP_LABEL: Record<RowFilterOp, string> = {
  IS: "is",
  IS_ONE_OF: "is one of",
  CONTAINS: "contains",
};

const TabAccessEditor: React.FC<{
  tabs: string[];
  columnsByTab: Record<string, string[]>;
  rules: TabRule[];
  disabled?: boolean;
  namePrefix: string;
  onChange: (rules: TabRule[]) => void;
}> = ({ tabs, columnsByTab, rules, disabled, namePrefix, onChange }) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  if (tabs.length === 0) {
    return (
      <p className="text-xs text-od-muted">This panel has no tables yet.</p>
    );
  }
  const aligned = alignTabRules(tabs, rules);
  const replace = (tab: string, next: TabRule) =>
    onChange(aligned.map((row) => (row.tab === tab ? next : row)));

  return (
    <div className="space-y-3" data-testid="tab-access">
      {aligned.map((rule) => {
        const columns = columnsByTab[rule.tab] ?? [];
        const known = new Set(columns);
        const selected = rule.filter?.column;
        const columnOptions =
          selected && !known.has(selected) ? [selected, ...columns] : columns;
        const email = !!rule.filter && usesMemberEmail(rule.filter.value);
        const draftText =
          rule.filter && drafts[rule.tab] !== undefined
            ? drafts[rule.tab]
            : rule.filter
              ? filterValueDraft(rule.filter)
              : "";
        return (
          <div key={rule.tab} className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-32 flex-1 truncate text-sm text-od-text">
                {rule.tab}
              </span>
              <div className="flex flex-wrap gap-1">
                {TAB_LEVELS.map((level) => (
                  <label
                    key={level}
                    className={`inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs sm:min-h-0 sm:py-1 [@media(pointer:coarse)]:min-h-11 ${
                      rule.level === level
                        ? "border-od-primary text-od-text"
                        : "border-od-border text-od-muted"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`${namePrefix}-${rule.tab}`}
                      checked={rule.level === level}
                      disabled={disabled}
                      aria-label={`${rule.tab} ${tabLevelLabel(level)}`}
                      onChange={() => {
                        if (level === "NONE") {
                          replace(rule.tab, { tab: rule.tab, level });
                          return;
                        }
                        replace(
                          rule.tab,
                          rule.filter
                            ? { tab: rule.tab, level, filter: rule.filter }
                            : { tab: rule.tab, level },
                        );
                      }}
                      className="accent-od-primary"
                    />
                    {tabLevelLabel(level)}
                  </label>
                ))}
              </div>
            </div>
            {(rule.level === "VIEW" || rule.level === "EDIT") && (
              <fieldset
                className="border-0 p-0"
                data-testid={`row-filter-${rule.tab}`}
              >
                <legend className="sr-only">Row rule for {rule.tab}</legend>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-od-muted">Only rows where</span>
                  <select
                    aria-label={`${rule.tab} column`}
                    className={fieldClass}
                    disabled={disabled}
                    value={selected ?? ""}
                    onChange={(event) => {
                      const column = event.target.value;
                      if (!column) {
                        setDrafts((current) => {
                          const next = { ...current };
                          delete next[rule.tab];
                          return next;
                        });
                        replace(rule.tab, {
                          tab: rule.tab,
                          level: rule.level,
                        });
                        return;
                      }
                      const previous = rule.filter ?? {
                        column,
                        op: "IS" as const,
                        value: "",
                      };
                      replace(rule.tab, {
                        tab: rule.tab,
                        level: rule.level,
                        filter: { ...previous, column },
                      });
                    }}
                  >
                    <option value="">Every row</option>
                    {columnOptions.map((name) => (
                      <option key={name} value={name}>
                        {known.has(name) ? name : `${name} (not in this table)`}
                      </option>
                    ))}
                  </select>
                  {rule.filter && (
                    <>
                      <select
                        aria-label={`${rule.tab} comparison`}
                        className={fieldClass}
                        disabled={disabled}
                        value={rule.filter.op}
                        onChange={(event) => {
                          const op = event.target.value as RowFilterOp;
                          const next = filterWithOp(rule.filter!, op);
                          setDrafts((current) => ({
                            ...current,
                            [rule.tab]: filterValueDraft(next),
                          }));
                          replace(rule.tab, {
                            tab: rule.tab,
                            level: rule.level,
                            filter: next,
                          });
                        }}
                      >
                        {ROW_FILTER_OPS.map((op) => (
                          <option key={op} value={op}>
                            {OP_LABEL[op]}
                          </option>
                        ))}
                      </select>
                      <Input
                        aria-label={`${rule.tab} value`}
                        className="min-w-32 flex-1"
                        size="sm"
                        disabled={disabled || email}
                        placeholder={
                          rule.filter.op === "IS_ONE_OF"
                            ? "North, South"
                            : "North"
                        }
                        value={email ? "" : draftText}
                        onChange={(event) => {
                          const text = event.target.value;
                          setDrafts((current) => ({
                            ...current,
                            [rule.tab]: text,
                          }));
                          replace(rule.tab, {
                            tab: rule.tab,
                            level: rule.level,
                            filter: filterFromDraft(rule.filter!, text),
                          });
                        }}
                      />
                      <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-xs text-od-text">
                        <input
                          type="checkbox"
                          className="accent-od-primary"
                          checked={email}
                          disabled={disabled}
                          aria-label={`${rule.tab} uses the member's email`}
                          onChange={(event) => {
                            const next = event.target.checked
                              ? filterAsMemberEmail(rule.filter!)
                              : filterWithoutMemberEmail(rule.filter!);
                            setDrafts((current) => {
                              const copy = { ...current };
                              delete copy[rule.tab];
                              return copy;
                            });
                            replace(rule.tab, {
                              tab: rule.tab,
                              level: rule.level,
                              filter: next,
                            });
                          }}
                        />
                        The member's email
                      </label>
                    </>
                  )}
                </div>
              </fieldset>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Members: React.FC = () => {
  const { datasourceId } = useParams<{ datasourceId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { datasources, isInitialLoad, fetchDatasources } = useDatasources();
  const api = useMemo(() => getApi({ token }), [token]);

  const datasource = datasources.find((ds) => ds.id === datasourceId);
  const projectId = (datasource as any)?.projectId as string | undefined;
  // The guest list is cached before sign-in and has no projectId. Refresh
  // once so Send invite can read the project the link just created. Skip the
  // first load: the list is still empty, and that request would use up the
  // one refresh. Reset when the datasource changes. Do not refresh again
  // while the row still has no project, or this effect would loop.
  const refreshedMissingProject = useRef(false);
  useEffect(() => {
    refreshedMissingProject.current = false;
  }, [datasourceId]);
  useEffect(() => {
    if (
      !datasourceId ||
      !token ||
      isInitialLoad ||
      projectId ||
      refreshedMissingProject.current
    ) {
      return;
    }
    refreshedMissingProject.current = true;
    void fetchDatasources();
  }, [datasourceId, token, projectId, isInitialLoad, fetchDatasources]);
  const viewerRole = (datasource as { viewerRole?: ViewerRole } | undefined)
    ?.viewerRole;
  // The members list is already owner-only. Hide the control when the
  // datasource names a non-owner role.
  const showPreview = !viewerRole || canManageMembers(viewerRole);
  const panelName = datasource?.name || "this panel";
  const tables = useMemo(() => {
    const attempt = latestSuccessAttempt(
      (datasource as { analyzeAttempts?: { status?: string }[] })?.analyzeAttempts,
    ) as
      | {
          databaseCollections?: {
            name?: string;
            fields?: { name?: string }[];
          }[];
        }
      | undefined;
    return (attempt?.databaseCollections ?? [])
      .filter((collection) => !!collection.name)
      .map((collection) => ({
        tab: collection.name as string,
        columns: (collection.fields ?? [])
          .map((field) => field.name)
          .filter((name): name is string => !!name),
      }));
  }, [datasource]);
  const tabNames = useMemo(() => tables.map((table) => table.tab), [tables]);
  const columnsByTab = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const table of tables) map[table.tab] = table.columns;
    return map;
  }, [tables]);

  const [emails, setEmails] = useState("");
  const [role, setRole] = useState<InvitableRole>(DEFAULT_INVITE_ROLE);
  const [tabRules, setTabRules] = useState<TabRule[]>([]);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [draftRole, setDraftRole] = useState<InvitableRole>(DEFAULT_INVITE_ROLE);
  const [draftRules, setDraftRules] = useState<TabRule[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loadState, setLoadState] = useState<
    "loading" | "ready" | "forbidden" | "error"
  >("loading");
  const [lastLink, setLastLink] = useState("");
  const [busyId, setBusyId] = useState("");
  const [previewBusyId, setPreviewBusyId] = useState("");

  const openPreview = async (member: Member) => {
    if (!datasourceId) return;
    setPreviewBusyId(member.id);
    try {
      const response = await (
        api as {
          request: (params: {
            path: string;
            method: string;
            query: { previewAs: string };
            format: string;
          }) => Promise<{ data: unknown }>;
        }
      ).request({
        path: `/datasources/${encodeURIComponent(datasourceId)}/preview`,
        method: "GET",
        query: { previewAs: member.id },
        format: "json",
      });
      const view = parsePreviewBody(response.data);
      const tab = view?.tabs[0]?.tab;
      if (!view || !tab) {
        toast.error("This member has no tables.");
        return;
      }
      rememberPreviewName(member.id, memberDisplayName(member));
      navigate(
        hrefWithPreview(
          `/datasources/${datasourceId}/collections/${encodeURIComponent(tab)}`,
          member.id,
        ),
      );
    } catch (error) {
      toast.error(
        previewFailureMessage(error) || "Could not open this preview.",
      );
    } finally {
      setPreviewBusyId("");
    }
  };

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await (api as any).request({
        path: `/projects/${projectId}/invites`,
        method: "GET",
        format: "json",
      });
      setMembers(sortMembers((res.data ?? []) as Member[]));
      setLoadState("ready");
    } catch (error: any) {
      setLoadState(error?.response?.status === 403 ? "forbidden" : "error");
    }
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const memberParam = searchParams.get("member");
  useEffect(() => {
    if (loadState !== "ready" || !memberParam) return;
    const member = members.find((row) => row.id === memberParam);
    if (member && !isOwnerRow(member)) {
      const current = accessOf(member);
      const next: InvitableRole =
        current === "EDITOR" || current === "CUSTOM" ? current : "VIEWER";
      setEditingId(member.id);
      setDraftRole(next);
      setDraftRules(alignTabRules(tabNames, member.tabRules));
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("member");
        return next;
      },
      { replace: true },
    );
  }, [loadState, memberParam, members, setSearchParams, tabNames]);

  const parsed = parseEmailList(emails);
  const inviteRules = alignTabRules(tabNames, tabRules);
  const inviteGap = rowFilterGapMessage(incompleteRowFilters(inviteRules));
  const draftAligned = alignTabRules(tabNames, draftRules);
  const draftGap = rowFilterGapMessage(incompleteRowFilters(draftAligned));
  const brokenMember = firstBrokenMember(members);
  const brokenLabel = brokenRuleBannerLabel(totalBrokenRules(members));

  const send = async () => {
    if (!projectId || parsed.emails.length === 0 || inviteGap) return;
    if (parsed.invalid.length > 0) {
      toast.error(`Not an email address: ${parsed.invalid.join(", ")}`);
      return;
    }
    setSending(true);
    // One request per address: the endpoint takes one invite, and a partial
    // failure must still leave the addresses that worked invited.
    const failed: string[] = [];
    let lastToken = "";
    for (const email of parsed.emails) {
      try {
        const res = await (api as any).request({
          path: `/projects/${projectId}/invites`,
          method: "POST",
          body: {
            email,
            ...accessRequestBody(role, inviteRules),
          },
          type: "application/json",
          format: "json",
        });
        const sent = res.data?.emailSent === true;
        if (res.data?.token) lastToken = res.data.token;
        toast.success(inviteNotice(email, sent, role));
      } catch (error: any) {
        failed.push(email);
        toast.error(
          error.response?.data?.message || `Could not invite ${email}`,
        );
      }
    }
    setLastLink(lastToken ? inviteLink(lastToken) : "");
    setEmails(failed.join(", "));
    setSending(false);
    load();
  };

  const changeAccess = async (
    member: Member,
    next: InvitableRole,
    rules: TabRule[] = [],
  ) => {
    if (!projectId) return;
    if (
      next === "CUSTOM" &&
      rowFilterGapMessage(incompleteRowFilters(alignTabRules(tabNames, rules)))
    ) {
      return;
    }
    setBusyId(member.id);
    try {
      await (api as any).request({
        path: `/projects/${projectId}/invites/${member.id}`,
        method: "PATCH",
        body: accessRequestBody(next, alignTabRules(tabNames, rules)),
        type: "application/json",
        format: "json",
      });
      toast.success(
        `${memberDisplayName(member)} is now ${roleLabel(next).toLowerCase()}.`,
      );
      setEditingId("");
      load();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Could not change that access",
      );
    } finally {
      setBusyId("");
    }
  };

  const openAccess = (member: Member) => {
    const current = accessOf(member);
    const next: InvitableRole =
      current === "EDITOR" || current === "CUSTOM" ? current : "VIEWER";
    setEditingId(member.id);
    setDraftRole(next);
    setDraftRules(alignTabRules(tabNames, member.tabRules));
  };

  const resend = async (member: Member) => {
    if (!projectId) return;
    setBusyId(member.id);
    try {
      const res = await (api as any).request({
        path: `/projects/${projectId}/invites`,
        method: "POST",
        // No role: resend rotates the link and keeps the access the owner set.
        body: { email: member.email },
        type: "application/json",
        format: "json",
      });
      if (res.data?.token) setLastLink(inviteLink(res.data.token));
      toast.success(
        res.data?.emailSent === true
          ? `Invite sent again to ${member.email}.`
          : `New invite link ready for ${member.email}.`,
      );
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not resend");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (member: Member) => {
    if (!projectId) return;
    setBusyId(member.id);
    try {
      await (api as any).request({
        path: `/projects/${projectId}/invites/${member.id}`,
        method: "DELETE",
      });
      toast.success(`${memberDisplayName(member)} no longer has access.`);
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not remove");
    } finally {
      setBusyId("");
    }
  };

  const copyLink = async () => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the invite link");
    }
  };

  if (!datasource) {
    return (
      <div className="p-6 text-sm text-od-muted">
        {isInitialLoad
          ? "Loading…"
          : "That panel is not in your workspace."}
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div className="max-w-2xl space-y-2 p-6">
        <h1 className="m-0 text-lg font-semibold text-od-text">Share</h1>
        <p className="text-sm text-od-muted">
          Only the owner of {panelName} can invite people and change access.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 p-4 md:p-6">
      <header className="space-y-1.5">
        {/* A panel's landing route is a real dashboard id, which this page
            does not know, so Back is history rather than a built path. */}
        <Button variant="link" size="sm" onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to {panelName}
        </Button>
        <h1 className="m-0 text-lg font-semibold text-od-text">
          Share {panelName}
        </h1>
        <p className="text-sm text-od-muted">
          People you invite see the tables, not the connection string. They need
          no Google account and no access to the sheet.
        </p>
      </header>

      <section className="space-y-3 rounded-xl border border-od-border bg-od-surface p-4">
        <h2 className="m-0 text-sm font-semibold text-od-text">Invite people</h2>

        <div className="space-y-1.5">
          <label
            htmlFor="invite-emails"
            className="block text-xs font-medium text-od-muted"
          >
            Email addresses
          </label>
          <Input
            id="invite-emails"
            value={emails}
            onChange={(event) => setEmails(event.target.value)}
            placeholder="sam@company.com, ada@company.com"
            disabled={sending}
          />
          <p className="text-xs text-od-muted">
            Separate addresses with a comma or a space.
          </p>
        </div>

        <fieldset className="space-y-1.5 border-0 p-0">
          <legend className="mb-1 block p-0 text-xs font-medium text-od-muted">
            Access
          </legend>
          <div className="flex flex-wrap gap-2">
            {INVITABLE_ROLES.map((option) => (
              <label
                key={option}
                className={`inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm transition-colors sm:min-h-0 sm:py-2 [@media(pointer:coarse)]:min-h-11 ${
                  role === option
                    ? "border-od-primary bg-[color-mix(in_srgb,var(--od-primary)_10%,transparent)] text-od-text"
                    : "border-od-border text-od-muted hover:bg-od-surface-2"
                }`}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={option}
                  checked={role === option}
                  onChange={() => {
                    setRole(option);
                    if (option === "CUSTOM") {
                      setTabRules((current) => alignTabRules(tabNames, current));
                    }
                  }}
                  disabled={sending}
                  className="accent-od-primary"
                />
                {roleChoiceLabel(option)}
              </label>
            ))}
          </div>
          {role === "CUSTOM" && (
            <TabAccessEditor
              tabs={tabNames}
              columnsByTab={columnsByTab}
              rules={tabRules}
              disabled={sending}
              namePrefix="invite-tab"
              onChange={setTabRules}
            />
          )}
          {/* One line, so the owner knows what they are granting before they
              grant it. */}
          <p className="text-xs text-od-muted" data-testid="role-preview">
            {rolePreviewLine(role, inviteRules)}
          </p>
          {role === "CUSTOM" && inviteGap && (
            <p className="text-xs text-od-danger" role="alert">
              {inviteGap}
            </p>
          )}
        </fieldset>

        <Button
          onClick={send}
          variant="primary"
          loading={sending}
          disabled={parsed.emails.length === 0 || !!inviteGap}
        >
          {parsed.emails.length > 1
            ? `Send ${parsed.emails.length} invites`
            : "Send invite"}
        </Button>

        {lastLink && (
          // With no SMTP the link is the invite, not a fallback, so it stays
          // open rather than behind a disclosure.
          <div className="space-y-1.5 rounded-lg border border-od-primary bg-od-bg p-2.5">
            <p className="m-0 text-sm font-medium text-od-text">Invite link</p>
            <div className="flex items-center gap-1.5">
              <span
                className="min-w-0 flex-1 truncate rounded border border-od-border bg-od-bg px-2 py-1 font-mono text-xs text-od-text"
                title={lastLink}
              >
                {lastLink}
              </span>
              <Button onClick={copyLink} variant="primary" size="sm">
                Copy
              </Button>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="m-0 text-sm font-semibold text-od-text">
          People with access
        </h2>

        {loadState === "ready" && brokenMember && brokenLabel && (
          <BrokenRuleBanner
            label={brokenLabel}
            memberName={memberDisplayName(brokenMember)}
            onClick={() => openAccess(brokenMember)}
          />
        )}

        {loadState === "loading" && (
          <p className="text-sm text-od-muted">Loading…</p>
        )}
        {loadState === "error" && (
          <p className="text-sm text-od-warning">
            Could not load the member list. Reload the page to try again.
          </p>
        )}

        {loadState === "ready" && (
          <ul className="m-0 list-none space-y-2 p-0">
            {members.map((member) => (
              <li
                key={member.id}
                data-testid="member-row"
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-od-border bg-od-surface p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate text-sm font-medium text-od-text">
                    {memberDisplayName(member)}
                  </p>
                  <p className="m-0 truncate text-xs text-od-muted">
                    {memberListSummary(member)}
                    {" · "}
                    {member.accepted
                      ? `Last active ${lastActiveLabel(member.lastActiveAt).toLowerCase()}`
                      : "Invite not accepted yet"}
                  </p>
                </div>

                <span className="shrink-0 rounded-full border border-od-border px-2 py-0.5 text-xs font-medium text-od-muted">
                  {accessOf(member) === "CUSTOM" ? "Custom" : member.roleLabel}
                </span>

                {member.editAccessRequested && (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={busyId === member.id}
                    onClick={() => changeAccess(member, "EDITOR")}
                  >
                    Approve edit access
                  </Button>
                )}

                {!isOwnerRow(member) && showPreview && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={previewBusyId === member.id}
                      onClick={() => openPreview(member)}
                      data-testid="preview-as"
                    >
                      {previewButtonLabel(memberDisplayName(member))}
                    </Button>
                    {!member.editAccessRequested && (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={busyId === member.id && editingId !== member.id}
                        onClick={() => openAccess(member)}
                      >
                        Change access
                      </Button>
                    )}
                    {editingId === member.id && (
                      <div className="basis-full space-y-2 border-t border-od-border pt-2">
                        <div className="flex flex-wrap gap-2">
                          {INVITABLE_ROLES.map((option) => (
                            <label
                              key={option}
                              className={`inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm sm:min-h-0 sm:py-2 [@media(pointer:coarse)]:min-h-11 ${
                                draftRole === option
                                  ? "border-od-primary text-od-text"
                                  : "border-od-border text-od-muted"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`change-role-${member.id}`}
                                value={option}
                                checked={draftRole === option}
                                onChange={() => {
                                  setDraftRole(option);
                                  if (option === "CUSTOM") {
                                    setDraftRules((current) =>
                                      alignTabRules(tabNames, current),
                                    );
                                  }
                                }}
                                className="accent-od-primary"
                              />
                              {roleChoiceLabel(option)}
                            </label>
                          ))}
                        </div>
                        {draftRole === "CUSTOM" && (
                          <TabAccessEditor
                            tabs={tabNames}
                            columnsByTab={columnsByTab}
                            rules={draftRules}
                            disabled={busyId === member.id}
                            namePrefix={`change-tab-${member.id}`}
                            onChange={setDraftRules}
                          />
                        )}
                        <p className="text-xs text-od-muted">
                          {rolePreviewLine(draftRole, draftAligned)}
                        </p>
                        {draftRole === "CUSTOM" && draftGap && (
                          <p className="text-xs text-od-danger" role="alert">
                            {draftGap}
                          </p>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          loading={busyId === member.id}
                          disabled={draftRole === "CUSTOM" && !!draftGap}
                          onClick={() =>
                            changeAccess(member, draftRole, draftRules)
                          }
                        >
                          Save access
                        </Button>
                      </div>
                    )}
                    {!member.accepted && (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={busyId === member.id}
                        onClick={() => resend(member)}
                      >
                        Resend
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      loading={busyId === member.id}
                      onClick={() => remove(member)}
                    >
                      Remove
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Members;
